import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../components/styles/BlogPage.css";

// 블로그 — 목록과 글 한 편을 같은 컴포넌트가 맡는다.
// /blog 는 목록, /blog/{slug} 는 그 글. 사례 페이지와 달리 글이 길어서
// 목록 안에서 펼치지 않고 화면을 바꾼다.

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

const BlogPage = () => {
    const navigate = useNavigate();
    const { slug } = useParams();

    const [posts, setPosts] = React.useState([]);
    const [post, setPost] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);

    const go = (path) => {
        navigate(path);
        window.scrollTo(0, 0);
    };

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    // 목록은 항상 받아 둔다. 글에서 '다른 글' 을 보여줄 때 쓴다.
    React.useEffect(() => {
        let alive = true;
        fetch("/api/posts")
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => { if (alive) setPosts(Array.isArray(d) ? d : []); })
            .catch((e) => { console.error("글 목록 조회 실패", e); if (alive) setFailed(true); })
            .finally(() => { if (alive && !slug) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!slug) { setPost(null); setLoading(false); return; }
        let alive = true;
        setLoading(true);
        fetch(`/api/posts/${encodeURIComponent(slug)}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => { if (alive) { setPost(d); setFailed(false); } })
            .catch((e) => { console.error("글 조회 실패", e); if (alive) setFailed(true); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [slug]);

    // ------------------------------------------------------------- 글 한 편
    if (slug) {
        if (loading) {
            return <div className="sch-wrap sch-blogwrap"><p className="sc-caption">불러오는 중…</p></div>;
        }
        if (failed || !post) {
            return (
                <div className="sch-wrap sch-blogwrap">
                    <div className="sc-empty">
                        <div className="sc-empty__title">글을 찾을 수 없습니다</div>
                        <div className="sc-empty__desc">
                            <button className="sch-inlinelink" onClick={() => go("/blog")}>목록으로 ↗</button>
                        </div>
                    </div>
                </div>
            );
        }

        const others = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

        return (
            <article className="sch-wrap sch-blogwrap">
                <button className="sch-inlinelink sch-blogback" onClick={() => go("/blog")}>← 목록</button>

                <header className="sch-bloghead">
                    <h1>{post.title}</h1>
                    <div className="sch-blogmeta">
                        <span className="sch-mono">{fmtDate(post.publishedAt)}</span>
                        {post.tags?.length > 0 && (
                            <span className="sch-blogtags">
                                {post.tags.map((t) => <span key={t} className="sc-badge sc-badge--best">{t}</span>)}
                            </span>
                        )}
                    </div>
                </header>

                {post.coverImage && (
                    <img className="sch-blogcover" src={post.coverImage} alt="" loading="lazy" />
                )}

                <div className="sch-blogbody">
                    {/* react-markdown 은 기본적으로 원시 HTML 을 렌더하지 않는다.
                        본문은 관리자만 쓰지만, 그래도 HTML 을 켜지 않는 쪽이 안전하다. */}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content || ""}</ReactMarkdown>
                </div>

                <div className="sch-sectioncta">
                    <span>비슷한 업무를 자동화하고 싶으신가요?</span>
                    <button className="sc-btn" onClick={() => go("/custom-product-first")}>
                        무료 견적 받기
                    </button>
                </div>

                {others.length > 0 && (
                    <section className="sch-blogmore">
                        <h2 className="sc-h4">다른 글</h2>
                        <div className="sch-pi">
                            {others.map((p) => (
                                <div key={p.slug} className="sch-pirow" role="button"
                                     onClick={() => go(`/blog/${p.slug}`)}>
                                    <span className="sch-code">{fmtDate(p.publishedAt)}</span>
                                    <span className="sch-nm">{p.title}</span>
                                    <span className="sch-ds">{p.summary}</span>
                                    <span className="sch-ar">→</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </article>
        );
    }

    // --------------------------------------------------------------- 목록
    return (
        <>
            <div className="sch-ph">
                <div className="sch-wrap">
                    <div className="sch-no">BLOG — 자동화 이야기</div>
                    <h1>자동화 이야기</h1>
                    <p className="sc-body-lg">
                        반복 업무를 자동화하면서 겪은 문제와 푼 방법을 적습니다.
                    </p>
                </div>
            </div>

            <div className="sch-wrap" style={{ paddingTop: "36px", paddingBottom: "72px" }}>
                {loading ? (
                    <p className="sc-caption">불러오는 중…</p>
                ) : failed ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">글을 불러오지 못했습니다</div>
                        <div className="sc-empty__desc">잠시 후 다시 시도해 주세요.</div>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">아직 올린 글이 없습니다</div>
                        <div className="sc-empty__desc">
                            <button className="sch-inlinelink" onClick={() => go("/portfolio")}>
                                수행 사례 보기 ↗
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="sch-pi">
                        {posts.map((p) => (
                            <div key={p.slug} className="sch-pirow" role="button"
                                 onClick={() => go(`/blog/${p.slug}`)}>
                                <span className="sch-code">{fmtDate(p.publishedAt)}</span>
                                <span className="sch-nm">{p.title}</span>
                                <span className="sch-ds">{p.summary}</span>
                                <span className="sch-ar">→</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default BlogPage;
